'use client';

import { useEffect, useState } from 'react';

import type { PopStoreSubscriptionLevel } from '@ps/models';

import { ExpressApplePayGate } from '../../checkout/components/express-apple-pay/ExpressApplePayGate';
import type { CheckoutIntent } from '../../checkout/types/CheckoutIntent';
import type { ResolveSubscribeExpressIntent } from '../types/SubscribeExpressIntent';

interface Props {
  resolveExpressIntent: ResolveSubscribeExpressIntent;
  urlCode: string;
  /** The selected PAID tier level (subscriber / fan / vip). */
  plan: PopStoreSubscriptionLevel;
  commitment: 'default' | 'long';
  agreeToOffers: boolean;
  consentToSharePhone: boolean;
  redirectTo?: string;
}

/**
 * Express "Subscribe with Apple Pay" for the bio subscribe picker. Unlike tipping /
 * PDP, a community subscription's intent is login-gated + backed by subscription
 * data, so it can't be resolved on the client or gossiped — it's resolved via a
 * server action for the current selection (plan / commitment / consents), and the
 * `ExpressApplePayGate` is KEYED on that selection so the engine is re-created when
 * it changes (the recurring plan is an intent-level fact, not mutable state).
 *
 * Renders nothing until the intent resolves (and nothing at all on a non-Apple-Pay
 * device — the gate handles that). The gate additionally waits for auth, but this
 * surface has already forced login, so the viewer is a real signed-in user.
 */
export function SubscribeExpressButton({
  resolveExpressIntent,
  urlCode,
  plan,
  commitment,
  agreeToOffers,
  consentToSharePhone,
  redirectTo,
}: Props) {
  const [intent, setIntent] = useState<CheckoutIntent | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIntent(null);
    void resolveExpressIntent({
      urlCode,
      plan,
      commitment,
      agreeToOffers,
      consentToSharePhone,
      redirectTo,
    })
      .then(resolved => {
        if (!cancelled) {
          setIntent(resolved);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIntent(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    resolveExpressIntent,
    urlCode,
    plan,
    commitment,
    agreeToOffers,
    consentToSharePhone,
    redirectTo,
  ]);

  if (!intent) {
    return null;
  }

  return (
    <ExpressApplePayGate
      key={`${plan}:${commitment}`}
      intent={intent}
      borderRadius={6}
    />
  );
}
